import React from 'react';
import {FixedSizeList, ListChildComponentProps} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import AutoSizer from 'react-virtualized-auto-sizer';
import {Alert, Spin} from 'antd';
import styled from 'styled-components';
import {useMediaQuery} from "@uidotdev/usehooks";
import {IJobDetails} from "../../../services/types.ts";
import {JobPoolItem} from "../components/JobPoolItem.tsx";
import {PromotionalCard} from "../components/PromotionalCard.tsx";

const ListContainerWrapper = styled.div`
    margin-top: 16px;
    flex: 1;
    min-height: 200px;
    height: calc(100vh - 150px);
    width: 100%;
    overflow: hidden;
`;

const LoadingSpinner = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    height: 100%;
`;

const ITEM_SIZE_DESKTOP = 150;
const ITEM_SIZE_MOBILE = 220;

// Define status types explicitly for clarity
type QueryStatus = 'pending' | 'error' | 'success' | 'idle';

interface VirtualizedJobsListProps {
    jobs: IJobDetails[];
    status: QueryStatus;
    error: Error | null;
    isFetchingNextPage: boolean;
    hasNextPage: boolean | undefined;
    itemCount: number; // Total items including potential loading placeholders
    loadMoreItems: () => void;
    isItemLoaded: (index: number) => boolean;
    listRef?: React.RefObject<FixedSizeList>;
    infiniteLoaderRef?: React.RefObject<InfiniteLoader>;
}

const Row: React.FC<ListChildComponentProps<IJobDetails[]>> = React.memo(({index, style, data: jobs}) => {
    const job = jobs[index];

    if (!job) {
        return (
            <div style={style}>
                <LoadingSpinner>
                    <Spin tip="Lade mehr Kandidaten..."/>
                </LoadingSpinner>
            </div>
        );
    }

    // Render promotional card for promotional items
    if (job.isPromotional || job.id === -1) {
        return <PromotionalCard style={style} />;
    }

    // Render actual job item
    return (
        <div style={style}>
            <JobPoolItem item={job} isJobSearchAutomatic={false}/>
        </div>
    );
});
Row.displayName = 'ListRow';


export const VirtualizedJobsList: React.FC<VirtualizedJobsListProps> = ({
                                                                            jobs,
                                                                            status,
                                                                            error,
                                                                            itemCount,
                                                                            loadMoreItems,
                                                                            isItemLoaded,
                                                                            listRef,
                                                                            infiniteLoaderRef,
                                                                        }) => {
    const isMobile = useMediaQuery("only screen and (max-width : 768px)");
    const itemSize = isMobile ? ITEM_SIZE_MOBILE : ITEM_SIZE_DESKTOP;

    if (status === 'pending') {
        return (
            <ListContainerWrapper>
                <LoadingSpinner><Spin size="large" tip="Lade Kandidaten..."/></LoadingSpinner>
            </ListContainerWrapper>
        );
    }

    if (status === 'error') {
        return (
            <ListContainerWrapper>
                <Alert message="Fehler beim Laden"
                       description={error?.message || 'Ein unbekannter Fehler ist aufgetreten.'} type="error" showIcon/>
            </ListContainerWrapper>
        );
    }


    return (
        <ListContainerWrapper>
            <AutoSizer>
                {({height, width}) => {
                    // Ensure AutoSizer provides valid dimensions
                    if (width === 0 || height === 0) {
                        return null;
                    }

                    return (
                        <InfiniteLoader
                            ref={infiniteLoaderRef}
                            isItemLoaded={isItemLoaded}
                            itemCount={itemCount}
                            loadMoreItems={loadMoreItems}
                            threshold={5} // How many items away from the end triggers loading
                        >
                            {({onItemsRendered, ref: infiniteLoaderInnerRef}) => (
                                <FixedSizeList
                                    ref={(el) => {
                                        // Assign ref for InfiniteLoader
                                        if (typeof infiniteLoaderInnerRef === 'function') {
                                            infiniteLoaderInnerRef(el);
                                        }
                                        // Assign ref for potential parent usage
                                        if (listRef && typeof listRef !== 'function') {
                                            (listRef as React.MutableRefObject<FixedSizeList | null>).current = el;
                                        }
                                    }}
                                    height={height}
                                    width={width}
                                    itemSize={itemSize}
                                    itemCount={itemCount}
                                    onItemsRendered={onItemsRendered}
                                    // Pass jobs data to the Row component
                                    itemData={jobs}
                                    // Use a stable key, ensure jobs has a unique 'key' or 'id'
                                    itemKey={(index, data) => data[index]?.key ?? data[index]?.id ?? `loading-${index}`}
                                    overscanCount={5} // Render items slightly outside the viewport
                                >
                                    {Row}
                                </FixedSizeList>
                            )}
                        </InfiniteLoader>
                    );
                }}
            </AutoSizer>
        </ListContainerWrapper>
    );
};
